import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAllAccommodations, useAllDestinations } from '../hooks/useCMS';
import { Accommodation } from '../types/cms';
import SafeImage from '../components/SafeImage';
import CountryFlag from '../components/CountryFlag';
import SEOHead from '../components/SEOHead';

function Alojamientos() {
  const { data, loading, error } = useAllAccommodations();
  const { data: destinationsData } = useAllDestinations();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const accommodations = data?.accommodations || [];
  const destinations = destinationsData?.destinations || [];

  // Obtener destinos únicos para el filtro
  const uniqueDestinations = useMemo(() => {
    const destSet = new Set<string>();
    accommodations.forEach(acc => {
      if (acc.destination?.title) {
        destSet.add(acc.destination.title);
      }
    });
    return Array.from(destSet).sort();
  }, [accommodations]);

  // Función para obtener el texto de la categoría
  const getCategoryLabel = (category?: string) => {
    const labels: Record<string, string> = {
      'basico': 'Básico',
      'medio': 'Medio',
      'superior': 'Superior',
    };
    return labels[category || ''] || 'Sin categoría';
  };

  // Función para obtener el color de la categoría
  const getCategoryColor = (category?: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary'> = {
      'basico': 'default',
      'medio': 'primary',
      'superior': 'secondary',
    };
    return colors[category || ''] || 'default';
  };

  // Filtrar alojamientos
  const filteredAccommodations = useMemo(() => {
    return accommodations.filter(acc => {
      // Filtro por término de búsqueda
      const matchesSearch = searchTerm === '' || 
        acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.destination?.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        acc.destination?.country?.name.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro por destino
      const matchesDestination = selectedDestination === '' ||
        acc.destination?.title === selectedDestination;

      // Filtro por categoría
      const matchesCategory = selectedCategory === '' ||
        acc.category === selectedCategory;

      return matchesSearch && matchesDestination && matchesCategory;
    });
  }, [accommodations, searchTerm, selectedDestination, selectedCategory]);

  const handleDestinationChange = (event: SelectChangeEvent) => {
    setSelectedDestination(event.target.value);
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error al cargar los alojamientos. Por favor, intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }

  const renderAccommodation = (acc: Accommodation) => (
    <Card key={acc.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SafeImage
        src={acc.coverImage?.publicUrl || acc.thumbnail?.publicUrl}
        alt={acc.name}
        width="100%"
        height={200}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="h2">
          {acc.name}
        </Typography>
        
        {/* Categoría */}
        {acc.category && (
          <Chip
            label={getCategoryLabel(acc.category)}
            color={getCategoryColor(acc.category)}
            size="small"
            sx={{ mb: 1 }}
          />
        )}

        {/* Ubicación */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
          {acc.destination?.country?.bandera && (
            <CountryFlag
              src={acc.destination.country.bandera.publicUrl}
              alt={acc.destination.country.name || 'Bandera del país'}
              width={20}
              height={14}
              sx={{ mr: 1 }}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {acc.destination?.title}
            {acc.destination?.country?.name && `, ${acc.destination.country.name}`}
          </Typography>
        </Box>

        {acc.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {acc.description.length > 150 
              ? `${acc.description.substring(0, 150)}...` 
              : acc.description}
          </Typography>
        )}

        {/* Paquetes relacionados */}
        {acc.packages && acc.packages.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {acc.packages.length} paquete{acc.packages.length !== 1 ? 's' : ''} disponible{acc.packages.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="primary">
              Desde ${Math.min(...acc.packages.map(p => p.priceFrom || 0).filter(p => p > 0)).toLocaleString().replace(/,/g, '.')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto', gap: 1 }}>
          {acc.packages && acc.packages.length > 0 && (
            <Button 
              variant="outlined" 
              color="primary"
              href={`/paquetes?alojamiento=${acc.name}`}
            >
              Ver paquetes
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary"
            href={`/alojamientos/${acc.slug}`}
          >
            Ver detalles
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <SEOHead
        title="Alojamientos en Destinos Turísticos"
        description="Encontrá el alojamiento perfecto para tu viaje. Hoteles, posadas y más opciones en los mejores destinos de Argentina, Brasil y Latinoamérica."
        keywords={[
          'alojamientos',
          'hoteles',
          'posadas',
          'alojamiento turístico',
          'hoteles en destinos turísticos',
        ]}
        url="/alojamientos"
        type="website"
      />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Alojamientos
      </Typography>
      
      {/* Filtros */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar alojamientos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select
                value={selectedDestination}
                label="Destino"
                onChange={handleDestinationChange}
              >
                <MenuItem value="">Todos los destinos</MenuItem>
                {uniqueDestinations.map((dest) => (
                  <MenuItem key={dest} value={dest}>
                    {dest}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={selectedCategory}
                label="Categoría"
                onChange={handleCategoryChange}
              >
                <MenuItem value="">Todas las categorías</MenuItem>
                <MenuItem value="basico">Básico</MenuItem>
                <MenuItem value="medio">Medio</MenuItem>
                <MenuItem value="superior">Superior</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Resultados */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {filteredAccommodations.length} alojamiento{filteredAccommodations.length !== 1 ? 's' : ''} encontrado{filteredAccommodations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Lista de alojamientos */}
      {filteredAccommodations.length > 0 ? (
        <Grid container spacing={3}>
          {filteredAccommodations.map((acc) => (
            <Grid item xs={12} sm={6} md={4} key={acc.id}>
              {renderAccommodation(acc)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" py={8}>
          <Typography variant="h5" color="text.secondary">
            No se encontraron alojamientos con los filtros seleccionados.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Intenta ajustar los filtros de búsqueda.
          </Typography>
        </Box>
      )}
    </Container>
    </>
  );
}

export default Alojamientos;
